import { useState, useEffect, useRef } from 'react';

interface KnowledgeTestProps {
  onFinish: (score: number) => void;
}

// ─── Food Classification Data ───────────────────────────────────────────────
const FOOD_ITEMS = [
  {
    id: 0, emoji: '🥕', label: 'Cà rốt tươi mới rửa', clean: true,
    explain: 'Cà rốt tươi, không sâu, không thối → thực phẩm tốt có thể dùng được!',
  },
  {
    id: 1, emoji: '🍎', label: 'Táo có sâu đục', clean: false,
    explain: 'Táo có sâu đục → vi khuẩn và nấm mốc đã vào bên trong, ăn vào có thể bị đau bụng!',
  },
  {
    id: 2, emoji: '🍚', label: 'Gạo nếp trắng', clean: true,
    explain: 'Gạo nếp trắng, không mốc, không mọt → thực phẩm tốt, có thể dùng để nấu!',
  },
  {
    id: 3, emoji: '🥛', label: 'Sữa quá hạn sử dụng', clean: false,
    explain: 'Sữa quá hạn có vi khuẩn sinh sôi nhiều → uống vào có thể bị ngộ độc!',
  },
  {
    id: 4, emoji: '🥬', label: 'Rau mới hái', clean: true,
    explain: 'Rau mới hái còn tươi, lá xanh, không héo → thực phẩm tốt, nên dùng!',
  },
  {
    id: 5, emoji: '🍚', label: 'Cơm thiu để qua đêm', clean: false,
    explain: 'Cơm thiu có mùi chua, vi khuẩn đã nhân lên nhiều → không được ăn!',
  },
  {
    id: 6, emoji: '🥚', label: 'Trứng tươi không nứt', clean: true,
    explain: 'Trứng tươi, vỏ còn nguyên vẹn → thực phẩm tốt, an toàn để nấu!',
  },
  {
    id: 7, emoji: '🍗', label: 'Thịt gà có mùi hôi', clean: false,
    explain: 'Thịt có mùi hôi là dấu hiệu đã hỏng, có nhiều vi khuẩn → tuyệt đối không được dùng!',
  },
  {
    id: 8, emoji: '🍌', label: 'Chuối chín vàng tươi', clean: true,
    explain: 'Chuối chín đều, vỏ vàng, không thối → thực phẩm tốt, an toàn!',
  },
  {
    id: 9, emoji: '🧅', label: 'Hành tây bị thối mềm', clean: false,
    explain: 'Hành tây thối mềm có nấm mốc bên trong → không được dùng, có hại cho sức khỏe!',
  },
];

// ─── Multiple Choice Questions ──────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    question: 'Khi rửa tay trước khi nấu ăn, cần chà xà phòng bao lâu?',
    options: ['5 giây', 'Ít nhất 20 giây', '1 phút', 'Không cần xà phòng'],
    correct: 1,
    explain: 'Chà xà phòng ít nhất 20 giây mới đủ để diệt vi khuẩn trên tay!',
  },
  {
    question: 'Ống nứa để nướng cơm lam phải như thế nào?',
    options: ['Ống nứa nứt để thoát hơi', 'Ống già vì chắc hơn', 'Ống vừa đủ tuổi, không nứt', 'Ống còn non để dẻo'],
    correct: 2,
    explain: 'Ống vừa đủ tuổi, không nứt: ống nứt cơm sẽ trào ra và cháy khi nướng!',
  },
  {
    question: 'Gạo nhồi vào ống nứa bao nhiêu là đúng?',
    options: ['Đầy ắp ống', 'Khoảng 2/3 ống', 'Chỉ 1/4 ống', '1/2 ống'],
    correct: 1,
    explain: 'Nhồi 2/3 ống thôi — gạo sẽ nở ra khi nấu, cần chỗ trống để cơm không tràn!',
  },
  {
    question: 'Khi nướng cơm lam, lửa quá to sẽ gây ra điều gì?',
    options: ['Cơm chín nhanh hơn, tốt hơn', 'Cơm cháy đen', 'Cơm vẫn sống', 'Ống nứa không sao'],
    correct: 1,
    explain: 'Lửa quá to → cơm cháy đen mất ngon! Cần giữ lửa vừa đủ để cơm chín vàng đều!',
  },
  {
    question: 'Tại sao cần xoay ống cơm lam khi nướng?',
    options: ['Để ống không bị vỡ', 'Để cơm chín đều tất cả các mặt', 'Để lửa cháy to hơn', 'Không cần xoay cũng được'],
    correct: 1,
    explain: 'Xoay đều để cơm chín đều tất cả các mặt — không xoay thì một bên cháy đen!',
  },
  {
    question: 'Điều gì sẽ xảy ra nếu ăn thực phẩm đã bị hỏng?',
    options: ['Không sao cả', 'Có thể đau bụng, ngộ độc thực phẩm', 'Tốt cho sức khoẻ', 'Làm cơm ngon hơn'],
    correct: 1,
    explain: 'Thực phẩm hỏng có vi khuẩn có thể gây đau bụng, nôn mửa và ngộ độc nguy hiểm!',
  },
];

// ─── Phase 1: Food Classification ───────────────────────────────────────────
function FoodClassificationPhase({ onDone }: { onDone: (score: number) => void }) {
  const [phase, setPhase] = useState<'intro' | 'play' | 'done'>('intro');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [showFeedback, setShowFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [feedbackData, setFeedbackData] = useState<{ isClean: boolean; explain: string; label: string; emoji: string } | null>(null);
  const [timeLeft, setTimeLeft] = useState(25);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shuffled = useRef([...FOOD_ITEMS].sort(() => Math.random() - 0.5)).current;

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const goNextItem = () => {
    const next = currentIdx + 1;
    if (next >= shuffled.length) {
      setPhase('done');
    } else {
      setCurrentIdx(next);
      setShowFeedback(null);
      setFeedbackData(null);
    }
  };

  const startTimer = () => {
    stopTimer();
    setTimeLeft(25);
    timerRef.current = setInterval(() => {
      setTimeLeft(v => {
        if (v <= 1) {
          stopTimer();
          goNextItem();
          return 25;
        }
        return v - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (phase === 'play' && showFeedback === null) {
      startTimer();
    }
    return stopTimer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentIdx, showFeedback]);

  const handleChoice = (isClean: boolean) => {
    stopTimer();
    const item = shuffled[currentIdx];
    const isCorrect = item.clean === isClean;
    if (isCorrect) {
      setCorrect(c => c + 1);
      setScore(s => s + 10);
      setShowFeedback('correct');
      setFeedbackData({ isClean: item.clean, explain: item.explain, label: item.label, emoji: item.emoji });
      setTimeout(goNextItem, 1500);
    } else {
      setShowFeedback('wrong');
      setFeedbackData({ isClean: item.clean, explain: item.explain, label: item.label, emoji: item.emoji });
      // WRONG: require manual button click to proceed
    }
  };

  if (phase === 'intro') return (
    <div className="flex flex-col items-center gap-6 p-8 bg-lime-50 rounded-3xl">
      <div className="text-8xl animate-bounce">🔍</div>
      <h3 className="text-3xl font-extrabold text-lime-700 text-center">Phân loại thực phẩm!</h3>
      <p className="text-lg text-lime-600 text-center max-w-lg">Thực phẩm lần lượt xuất hiện. Em bấm <strong>Được dùng</strong> nếu thực phẩm còn tốt, bấm <strong>Không dùng</strong> nếu thực phẩm đã hỏng!</p>
      <div className="grid grid-cols-2 gap-5 w-full max-w-lg">
        <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-5 text-center font-extrabold text-green-700 text-lg">✅ Được dùng</div>
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-5 text-center font-extrabold text-red-700 text-lg">❌ Không dùng</div>
      </div>
      <button onClick={() => setPhase('play')} className="px-12 py-5 bg-lime-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-lime-600 whitespace-nowrap">
        Bắt đầu nào! →
      </button>
    </div>
  );

  if (phase === 'done') {
    const pct = Math.round((correct / shuffled.length) * 100);
    return (
      <div className="flex flex-col items-center gap-6 p-8 bg-lime-50 rounded-3xl">
        <div className="text-8xl">{pct >= 80 ? '🌟' : pct >= 60 ? '😊' : '💪'}</div>
        <h3 className="text-3xl font-extrabold text-lime-700">Kết quả phân loại!</h3>
        <div className="bg-white rounded-2xl p-6 text-center w-full max-w-md border-2 border-lime-200">
          <p className="text-5xl font-extrabold text-lime-600 mb-2">{correct}/{shuffled.length}</p>
          <p className="text-lg text-gray-600">câu đúng — {pct}% chính xác</p>
          <p className="mt-4 font-extrabold text-lg text-lime-700">
            {pct >= 80 ? '🌟 Xuất sắc! Em nhận biết thực phẩm rất giỏi!' : pct >= 60 ? '😊 Tốt lắm! Hãy chú ý thêm nhé!' : '💪 Cần luyện tập thêm! Thực phẩm hỏng nguy hiểm lắm đó!'}
          </p>
        </div>
        <button onClick={() => onDone(score)} className="px-12 py-5 bg-lime-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-lime-600 whitespace-nowrap">
          Tiếp tục bài kiểm tra →
        </button>
      </div>
    );
  }

  const item = shuffled[currentIdx];
  const progress = (currentIdx / shuffled.length) * 100;
  return (
    <div className="flex flex-col items-center gap-5 p-6 bg-lime-50 rounded-3xl min-h-80">
      {/* Progress bar */}
      <div className="flex justify-between w-full max-w-xl items-center gap-4">
        <span className="text-base font-extrabold text-lime-700 whitespace-nowrap">{currentIdx + 1}/{shuffled.length}</span>
        <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-lime-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        {showFeedback === null && (
          <span className={`text-base font-extrabold whitespace-nowrap ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-lime-700'}`}>⏱️ {timeLeft}s</span>
        )}
      </div>

      {/* Item card */}
      <div className={`flex flex-col items-center gap-5 w-full max-w-xl p-8 rounded-3xl border-4 transition-all ${
        showFeedback === 'correct' ? 'bg-green-100 border-green-400' : showFeedback === 'wrong' ? 'bg-red-100 border-red-400' : 'bg-white border-lime-300'
      }`}>
        <span className="text-9xl">{item.emoji}</span>
        <p className="text-2xl font-extrabold text-gray-800 text-center">{item.label}</p>

        {showFeedback === 'correct' && (
          <div className="flex flex-col items-center gap-3">
            <p className="text-green-600 font-extrabold text-3xl">✅ Đúng rồi!</p>
            <div className="bg-green-50 border-2 border-green-300 rounded-2xl px-5 py-4 text-base font-bold text-green-800 text-center max-w-sm">
              💡 {feedbackData?.explain}
            </div>
          </div>
        )}

        {showFeedback === 'wrong' && feedbackData && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-red-600 font-extrabold text-3xl">❌ Sai rồi!</p>
            <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl px-6 py-5 text-lg font-semibold text-amber-900 text-center max-w-md">
              <p className="font-extrabold text-amber-700 mb-3 text-xl">💡 Đáp án đúng:</p>
              <p className="text-amber-800 text-lg">
                {feedbackData.isClean
                  ? `${feedbackData.emoji} "${feedbackData.label}" → Được dùng!`
                  : `${feedbackData.emoji} "${feedbackData.label}" → Không được dùng!`}
              </p>
              <p className="text-amber-700 mt-3 text-base">{feedbackData.explain}</p>
            </div>
            <button
              onClick={goNextItem}
              className="px-10 py-4 bg-amber-500 text-white font-extrabold text-lg rounded-2xl cursor-pointer hover:bg-amber-600 whitespace-nowrap">
              Đã hiểu, tiếp tục! →
            </button>
          </div>
        )}
      </div>

      {/* Answer buttons */}
      {!showFeedback && (
        <div className="flex gap-6 w-full max-w-xl">
          <button onClick={() => handleChoice(true)}
            className="flex-1 py-6 bg-green-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-green-600 active:scale-95 transition-all whitespace-nowrap">
            ✅ Được dùng
          </button>
          <button onClick={() => handleChoice(false)}
            className="flex-1 py-6 bg-red-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-red-600 active:scale-95 transition-all whitespace-nowrap">
            ❌ Không dùng
          </button>
        </div>
      )}
      <div className="flex items-center gap-4 text-base font-extrabold text-lime-700">
        <span>Điểm: {score}</span><span>•</span><span>Đúng: {correct}/{currentIdx}</span>
      </div>
    </div>
  );
}

// ─── Phase 2: Multiple Choice Quiz ──────────────────────────────────────────
function MultipleChoicePhase({ onDone }: { onDone: (score: number) => void }) {
  const [qIdx, setQIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);

  const q = QUIZ_QUESTIONS[qIdx];

  const handleSelect = (optIdx: number) => {
    if (showResult) return;
    setSelected(optIdx);
    setShowResult(true);
    const isCorrect = optIdx === q.correct;
    if (isCorrect) {
      setScore(s => s + 15);
      setCorrectCount(c => c + 1);
    }
  };

  const handleNext = () => {
    const next = qIdx + 1;
    if (next >= QUIZ_QUESTIONS.length) {
      onDone(score);
    } else {
      setQIdx(next);
      setSelected(null);
      setShowResult(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-7 bg-amber-50 rounded-3xl min-h-80">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-extrabold text-amber-700">📝 Câu hỏi {qIdx + 1}/{QUIZ_QUESTIONS.length}</h3>
        <span className="text-lg font-extrabold text-amber-600">⭐ {score} điểm</span>
      </div>
      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${((qIdx) / QUIZ_QUESTIONS.length) * 100}%` }} />
      </div>
      <div className="bg-white rounded-2xl p-6 border-2 border-amber-200">
        <p className="text-xl font-extrabold text-gray-800">{q.question}</p>
      </div>
      <div className="flex flex-col gap-4">
        {q.options.map((opt, i) => {
          let btnClass = 'border-2 border-gray-200 bg-white text-gray-700 hover:border-amber-400 hover:bg-amber-50';
          if (showResult) {
            if (i === q.correct) btnClass = 'border-2 border-green-500 bg-green-50 text-green-800';
            else if (i === selected && i !== q.correct) btnClass = 'border-2 border-red-400 bg-red-50 text-red-700';
            else btnClass = 'border-2 border-gray-100 bg-gray-50 text-gray-400 opacity-60';
          }
          return (
            <button
              key={i}
              onClick={() => handleSelect(i)}
              disabled={showResult}
              className={`w-full text-left px-6 py-5 rounded-2xl text-lg font-semibold transition-all cursor-pointer whitespace-normal ${btnClass} ${!showResult ? 'active:scale-98' : ''}`}
            >
              <span className="font-extrabold mr-2">{String.fromCharCode(65 + i)}.</span>{opt}
              {showResult && i === q.correct && <span className="ml-2">✅</span>}
              {showResult && i === selected && i !== q.correct && <span className="ml-2">❌</span>}
            </button>
          );
        })}
      </div>
      {showResult && (
        <div className={`rounded-2xl p-5 border-2 ${selected === q.correct ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
          <p className="text-base font-extrabold text-amber-800 mb-2">{selected === q.correct ? '🌟 Chính xác!' : '💡 Đáp án đúng:'}</p>
          <p className="text-base text-amber-700">{q.explain}</p>
        </div>
      )}
      {showResult && (
        <button onClick={handleNext} className="py-5 bg-amber-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-amber-600 whitespace-nowrap">
          {qIdx + 1 >= QUIZ_QUESTIONS.length ? '🏆 Xem kết quả!' : 'Câu tiếp theo →'}
        </button>
      )}
      <div className="text-base text-center text-amber-600 font-bold">Đã trả lời đúng: {correctCount}/{qIdx}</div>
    </div>
  );
}

// ─── Main KnowledgeTest ──────────────────────────────────────────────────────
export default function KnowledgeTest({ onFinish }: KnowledgeTestProps) {
  const [phase, setPhase] = useState<'food-class' | 'quiz' | 'summary'>('food-class');
  const [foodScore, setFoodScore] = useState(0);
  const [quizScore, setQuizScore] = useState(0);

  const handleFoodDone = (s: number) => {
    setFoodScore(s);
    setPhase('quiz');
  };

  const handleQuizDone = (s: number) => {
    setQuizScore(s);
    setPhase('summary');
  };

  if (phase === 'food-class') {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-lime-100 border-2 border-lime-300 rounded-2xl p-5 text-center">
          <p className="text-lg font-extrabold text-lime-700">📋 Bài kiểm tra gồm 2 phần — Phần 1: Phân loại thực phẩm</p>
          <div className="flex justify-center gap-4 mt-3">
            <span className="text-base px-4 py-2 bg-lime-500 text-white rounded-full font-extrabold">Phần 1: Phân loại</span>
            <span className="text-base px-4 py-2 bg-gray-200 text-gray-500 rounded-full font-bold">Phần 2: Câu hỏi</span>
          </div>
        </div>
        <FoodClassificationPhase onDone={handleFoodDone} />
      </div>
    );
  }

  if (phase === 'quiz') {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-amber-100 border-2 border-amber-300 rounded-2xl p-5 text-center">
          <p className="text-lg font-extrabold text-amber-700">📋 Phần 2: Câu hỏi kiến thức an toàn thực phẩm</p>
          <div className="flex justify-center gap-4 mt-3">
            <span className="text-base px-4 py-2 bg-green-500 text-white rounded-full font-extrabold">✅ Phần 1: {foodScore}đ</span>
            <span className="text-base px-4 py-2 bg-amber-500 text-white rounded-full font-extrabold">Phần 2: Câu hỏi</span>
          </div>
        </div>
        <MultipleChoicePhase onDone={handleQuizDone} />
      </div>
    );
  }

  const total = foodScore + quizScore;
  const maxScore = FOOD_ITEMS.length * 10 + QUIZ_QUESTIONS.length * 15;
  const pct = Math.round((total / maxScore) * 100);

  return (
    <div className="flex flex-col items-center gap-7 p-9 bg-gradient-to-b from-amber-50 to-orange-50 rounded-3xl">
      <div className="text-9xl animate-bounce">{pct >= 80 ? '🌟' : pct >= 60 ? '🎉' : '💪'}</div>
      <h3 className="text-3xl font-extrabold text-amber-700 text-center">Hoàn thành bài kiểm tra!</h3>
      <div className="bg-white rounded-3xl p-7 w-full max-w-md border-2 border-amber-200">
        <div className="text-center mb-6">
          <p className="text-6xl font-extrabold text-amber-600">{total}</p>
          <p className="text-lg text-gray-500">/ {maxScore} điểm ({pct}%)</p>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center bg-lime-50 rounded-2xl p-4">
            <span className="text-base font-extrabold text-lime-700">🔍 Phân loại thực phẩm</span>
            <span className="text-xl font-extrabold text-lime-600">{foodScore}đ</span>
          </div>
          <div className="flex justify-between items-center bg-amber-50 rounded-2xl p-4">
            <span className="text-base font-extrabold text-amber-700">📝 Câu hỏi kiến thức</span>
            <span className="text-xl font-extrabold text-amber-600">{quizScore}đ</span>
          </div>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden mt-5">
          <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all"
            style={{ width: `${pct}%` }} />
        </div>
        <p className="text-center font-extrabold text-lg text-amber-700 mt-5">
          {pct >= 80 ? '🌟 Xuất sắc! Em đã nắm rất chắc kiến thức!' : pct >= 60 ? '😊 Tốt lắm! Hãy ôn thêm nhé!' : '💪 Cần học thêm về an toàn thực phẩm nhé!'}
        </p>
      </div>
      <button onClick={() => onFinish(total)} className="px-14 py-6 bg-gradient-to-r from-amber-500 to-orange-400 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:opacity-90 whitespace-nowrap">
        🏆 Xem kết quả tổng!
      </button>
    </div>
  );
}
